import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FaSyncAlt, FaSearch, FaCar, FaMotorcycle, FaTruck, FaExclamationTriangle, FaMapMarkerAlt, FaQuestionCircle } from 'react-icons/fa';

const AZUL = '#1F467E';

export interface VeiculoLista {
    placa: string;
    marca: string;
    modelo: string;
    tipoVeiculo: number;
    nomeLocatario?: string | null;
    status: string;
    ultimaAtualizacao?: string | null;
    endereco?: string | null;
    ignicao?: boolean | null;
    emMovimento?: boolean | null;
    bloqueado?: boolean | null;
    velocidade?: number | null;
    latitude?: number | null;
    longitude?: number | null;
    odometroKm?: number | null;
    possuiPosicao: boolean;
}

type FiltroSinal = 'todos' | 'online' | 'semSinal' | 'semSinal24h';
type FiltroIgnicao = 'todas' | 'ligada' | 'desligada';

export const estaOnline = (v: VeiculoLista) => (v.status || '').toLowerCase() === 'online';

const horasDesde = (iso?: string | null): number | null => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (isNaN(t)) return null;
    return (Date.now() - t) / 36e5;
};

export const semSinalHa24h = (v: VeiculoLista) => {
    if (estaOnline(v)) return false;
    const horas = horasDesde(v.ultimaAtualizacao);
    return horas === null || horas >= 24;
};

const tempoRelativo = (iso?: string | null): string => {
    const horas = horasDesde(iso);
    if (horas === null) return 'nunca comunicou';
    const minutos = Math.round(horas * 60);
    if (minutos < 1) return 'agora mesmo';
    if (minutos < 60) return `há ${minutos} min`;
    if (horas < 48) return `há ${Math.round(horas)} h`;
    return `há ${Math.round(horas / 24)} dias`;
};

// Cache de geocodificação em nível de módulo: sobrevive à troca de abas (o componente é
// desmontado ao ir para o Mapa) e guarda também resultados vazios (cache negativo), para não
// re-tentar a mesma coordenada a cada poll. A trava única impede loops concorrentes.
const geocodeCache: Record<string, string> = {};
let geocodeLoopAtivo = false;

const chaveCoord = (lat: number, lng: number) => `${lat.toFixed(4)}_${lng.toFixed(4)}`;

const formatarDataHora = (iso?: string | null): string => {
    // new Date(inválido) não lança exceção: retorna Invalid Date, que viraria texto literal na tela.
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR');
};

/* ---------------------------------- estilos ---------------------------------- */

const Wrapper = styled.div`
    flex: 1;
    overflow-y: auto;
    background: #f5f7fa;
    padding: 16px 20px 32px;
`;

const BarraFiltros = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
`;

const BuscaBox = styled.div`
    position: relative;
    width: 280px;
    max-width: 100%;

    svg {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        color: #8a94a6;
        pointer-events: none;
    }

    input {
        width: 100%;
        padding: 8px 12px 8px 34px;
        font-size: 13px;
        border: 1px solid #d5d9e0;
        border-radius: 8px;
        box-sizing: border-box;
        outline: none;
        background: #fff;
    }
`;

const ChipFiltro = styled.button<{ $ativo: boolean; $alerta?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid ${p => p.$ativo ? (p.$alerta ? '#dc2626' : AZUL) : '#d5d9e0'};
    background: ${p => p.$ativo ? (p.$alerta ? '#dc2626' : AZUL) : '#fff'};
    color: ${p => p.$ativo ? '#fff' : (p.$alerta ? '#dc2626' : '#475569')};
    transition: all 0.15s;

    &:hover {
        border-color: ${p => p.$alerta ? '#dc2626' : AZUL};
    }
`;

const SelectIgnicao = styled.select`
    padding: 7px 10px;
    font-size: 12.5px;
    border: 1px solid #d5d9e0;
    border-radius: 8px;
    background: #fff;
    color: #475569;
    outline: none;
    cursor: pointer;
`;

const BotaoAjuda = styled.button`
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    border: none;
    border-radius: 6px;
    background: none;
    color: #64748b;
    font-size: 12.5px;
    cursor: pointer;
    text-decoration: underline dotted;
    text-underline-offset: 3px;

    &:hover { color: ${AZUL}; }
`;

const OverlayAjuda = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    z-index: 1400;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
`;

const ModalAjuda = styled.div`
    background: #fff;
    border-radius: 14px;
    max-width: 560px;
    width: 100%;
    max-height: 85vh;
    overflow-y: auto;
    padding: 24px 26px;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3);
`;

const ModalTitulo = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;

    h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
        color: #0f172a;
    }

    button {
        border: none;
        background: #f1f5f9;
        color: #475569;
        border-radius: 50%;
        width: 28px;
        height: 28px;
        cursor: pointer;
        font-size: 14px;
        line-height: 1;
    }
`;

const ItemAjuda = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 12px 0;
    border-bottom: 1px solid #f1f5f9;

    &:last-child { border-bottom: none; }

    p {
        margin: 0;
        font-size: 13px;
        color: #475569;
        line-height: 1.5;
    }

    p b { color: #0f172a; }
`;

const NotaAjuda = styled.p`
    margin: 14px 0 0;
    font-size: 12px;
    color: #94a3b8;
    line-height: 1.5;
`;

const BotaoAtualizar = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: 1px solid ${AZUL};
    border-radius: 6px;
    background: #fff;
    color: ${AZUL};
    font-size: 13px;
    cursor: pointer;

    &:disabled { opacity: 0.6; cursor: default; }
`;

const AlertaSemSinal = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    text-align: left;
    padding: 10px 14px;
    margin-bottom: 12px;
    border: 1px solid #fecaca;
    border-radius: 10px;
    background: #fef2f2;
    color: #991b1b;
    font-size: 13px;
    cursor: pointer;

    svg { flex-shrink: 0; color: #dc2626; }
    b { font-weight: 700; }
`;

const Tabela = styled.div`
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow-x: auto;

    table {
        width: 100%;
        border-collapse: collapse;
        min-width: 860px;
    }

    th {
        text-align: left;
        padding: 10px 14px;
        font-size: 11.5px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #64748b;
        border-bottom: 1px solid #e2e8f0;
        background: #f8fafc;
        white-space: nowrap;
    }

    td {
        padding: 10px 14px;
        font-size: 13px;
        color: #1e293b;
        border-bottom: 1px solid #f1f5f9;
        vertical-align: middle;
    }

    tr:last-child td { border-bottom: none; }
    tbody tr:hover td { background: #f8fafc; }
`;

const LinhaVeiculo = styled.tr<{ $alerta: boolean }>`
    ${p => p.$alerta && 'td { background: #fff7f7; }'}
`;

const CelVeiculo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    svg { color: #94a3b8; flex-shrink: 0; }
`;

const PlacaModelo = styled.div`
    display: flex;
    flex-direction: column;

    b { font-size: 13.5px; color: #0f172a; }
    span { font-size: 12px; color: #64748b; }
`;

const ChipStatus = styled.span<{ $cor: 'verde' | 'vermelho' | 'cinza' | 'ambar' }>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    background: ${p => ({ verde: '#d1fae5', vermelho: '#fee2e2', cinza: '#f1f5f9', ambar: '#fef3c7' }[p.$cor])};
    color: ${p => ({ verde: '#065f46', vermelho: '#991b1b', cinza: '#64748b', ambar: '#92400e' }[p.$cor])};

    &::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${p => ({ verde: '#10b981', vermelho: '#dc2626', cinza: '#94a3b8', ambar: '#f59e0b' }[p.$cor])};
    }
`;

const Atualizacao = styled.div<{ $alerta: boolean }>`
    display: flex;
    flex-direction: column;

    b {
        font-size: 13px;
        font-weight: ${p => p.$alerta ? 700 : 500};
        color: ${p => p.$alerta ? '#dc2626' : '#1e293b'};
    }
    span { font-size: 11.5px; color: #94a3b8; }
`;

const Vazio = styled.div`
    padding: 48px 24px;
    text-align: center;
    color: #64748b;
    font-size: 14px;
`;

const RodapeInfo = styled.div`
    margin-top: 10px;
    font-size: 12px;
    color: #94a3b8;
`;

/* --------------------------------- componente --------------------------------- */

const iconeTipo = (tipo: number) => {
    if (tipo === 2) return <FaMotorcycle size={16} />;
    if (tipo === 4) return <FaTruck size={16} />;
    return <FaCar size={16} />;
};

interface MeusVeiculosProps {
    veiculos: VeiculoLista[];
    carregando: boolean;
    ultimaConsulta: Date | null;
    onAtualizar: () => void;
}

export function MeusVeiculos({ veiculos, carregando, ultimaConsulta, onAtualizar }: MeusVeiculosProps) {
    const [busca, setBusca] = useState('');
    const [filtroSinal, setFiltroSinal] = useState<FiltroSinal>('todos');
    const [filtroIgnicao, setFiltroIgnicao] = useState<FiltroIgnicao>('todas');
    const [ajudaAberta, setAjudaAberta] = useState(false);
    const [enderecosResolvidos, setEnderecosResolvidos] = useState<Record<string, string>>({});

    // A Agillock nem sempre preenche o endereço da posição. Para esses casos, resolve por
    // geocodificação reversa (Nominatim, mesma técnica do popup do mapa) — em série, com
    // cache por coordenada, respeitando o rate limit de ~1 req/s do serviço.
    useEffect(() => {
        // Reaproveita o cache de módulo (ex.: voltou da aba Mapa) sem novas requisições.
        setEnderecosResolvidos(prev => {
            let mudou = false;
            const proximos = { ...prev };
            for (const v of veiculos) {
                if (v.endereco || v.latitude == null || v.longitude == null) continue;
                const chave = chaveCoord(v.latitude, v.longitude);
                const cacheado = geocodeCache[chave];
                if (cacheado && !proximos[chave]) { proximos[chave] = cacheado; mudou = true; }
            }
            return mudou ? proximos : prev;
        });

        const pendentes = veiculos.filter(v =>
            !v.endereco && v.latitude != null && v.longitude != null &&
            geocodeCache[chaveCoord(v.latitude, v.longitude)] === undefined
        );
        if (pendentes.length === 0 || geocodeLoopAtivo) return;
        geocodeLoopAtivo = true;

        let cancelado = false;
        (async () => {
            for (const v of pendentes) {
                if (cancelado) break;
                const chave = chaveCoord(v.latitude!, v.longitude!);
                if (geocodeCache[chave] !== undefined) continue;
                let endereco = '';
                try {
                    const resp = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${v.latitude}&lon=${v.longitude}&format=json&accept-language=pt-BR`);
                    const data = await resp.json();
                    if (data?.address) {
                        const a = data.address;
                        endereco = [a.road, a.house_number, a.suburb || a.neighbourhood, a.city || a.town || a.village, a.state]
                            .filter(Boolean).join(', ') || data.display_name || '';
                    }
                } catch { /* mantém o fallback de coordenadas */ }
                // Cache negativo incluso: re-tentar a mesma coordenada a cada poll viola o
                // rate limit do Nominatim e acaba em bloqueio do IP do cliente.
                geocodeCache[chave] = endereco;
                if (endereco && !cancelado) setEnderecosResolvidos(prev => ({ ...prev, [chave]: endereco }));
                await new Promise(r => setTimeout(r, 1100));
            }
            geocodeLoopAtivo = false;
        })();

        // A trava geocodeLoopAtivo é liberada apenas pelo próprio loop (que checa `cancelado` a
        // cada iteração): liberar aqui abriria espaço para dois loops simultâneos.
        return () => { cancelado = true; };
    }, [veiculos]);

    const contagens = useMemo(() => ({
        todos: veiculos.length,
        online: veiculos.filter(estaOnline).length,
        semSinal: veiculos.filter(v => !estaOnline(v)).length,
        semSinal24h: veiculos.filter(semSinalHa24h).length,
    }), [veiculos]);

    const filtrados = useMemo(() => {
        const q = busca.trim().toLowerCase();
        return veiculos
            .filter(v => {
                if (filtroSinal === 'online' && !estaOnline(v)) return false;
                if (filtroSinal === 'semSinal' && estaOnline(v)) return false;
                if (filtroSinal === 'semSinal24h' && !semSinalHa24h(v)) return false;
                if (filtroIgnicao === 'ligada' && v.ignicao !== true) return false;
                if (filtroIgnicao === 'desligada' && v.ignicao !== false) return false;
                if (!q) return true;
                return [v.placa, v.marca, v.modelo, v.nomeLocatario, v.endereco]
                    .some(campo => (campo || '').toLowerCase().includes(q));
            })
            .sort((a, b) => {
                // Sem sinal primeiro (mais antigos no topo) — é o que precisa de atenção.
                const aOn = estaOnline(a) ? 1 : 0;
                const bOn = estaOnline(b) ? 1 : 0;
                if (aOn !== bOn) return aOn - bOn;
                if (!aOn) {
                    const ta = a.ultimaAtualizacao ? new Date(a.ultimaAtualizacao).getTime() : 0;
                    const tb = b.ultimaAtualizacao ? new Date(b.ultimaAtualizacao).getTime() : 0;
                    if (ta !== tb) return ta - tb;
                }
                return (a.placa || '').localeCompare(b.placa || '');
            });
    }, [veiculos, busca, filtroSinal, filtroIgnicao]);

    const renderSinal = (v: VeiculoLista) => {
        if (estaOnline(v)) return <ChipStatus $cor="verde">Online</ChipStatus>;
        if (!v.possuiPosicao && !v.ultimaAtualizacao) return <ChipStatus $cor="vermelho">Nunca reportou</ChipStatus>;
        return <ChipStatus $cor={semSinalHa24h(v) ? 'vermelho' : 'ambar'}>Sem sinal</ChipStatus>;
    };

    const renderIgnicao = (v: VeiculoLista) => {
        if (v.ignicao === true) return <ChipStatus $cor="verde">Ligada</ChipStatus>;
        if (v.ignicao === false) return <ChipStatus $cor="cinza">Desligada</ChipStatus>;
        return <span style={{ color: '#94a3b8' }}>—</span>;
    };

    const renderEndereco = (v: VeiculoLista) => {
        if (v.endereco) return v.endereco;
        if (v.latitude != null && v.longitude != null) {
            const resolvido = enderecosResolvidos[chaveCoord(v.latitude, v.longitude)];
            if (resolvido) return resolvido;
            return <span style={{ color: '#94a3b8' }}>Localizando endereço...</span>;
        }
        return <span style={{ color: '#94a3b8' }}>Sem posição registrada</span>;
    };

    return (
        <Wrapper>
            {contagens.semSinal24h > 0 && filtroSinal !== 'semSinal24h' && (
                <AlertaSemSinal onClick={() => setFiltroSinal('semSinal24h')}>
                    <FaExclamationTriangle size={16} />
                    <span>
                        <b>{contagens.semSinal24h} veículo(s) sem comunicar há mais de 24 horas.</b>{' '}
                        Pode indicar rastreador desligado, sem energia ou violado. Clique para listar.
                    </span>
                </AlertaSemSinal>
            )}

            <BarraFiltros>
                <BuscaBox>
                    <FaSearch size={13} />
                    <input
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                        placeholder="Buscar placa, modelo, locatário ou endereço"
                    />
                </BuscaBox>
                <ChipFiltro $ativo={filtroSinal === 'todos'} onClick={() => setFiltroSinal('todos')}>
                    Todos ({contagens.todos})
                </ChipFiltro>
                <ChipFiltro $ativo={filtroSinal === 'online'} onClick={() => setFiltroSinal('online')}>
                    Online ({contagens.online})
                </ChipFiltro>
                <ChipFiltro $ativo={filtroSinal === 'semSinal'} $alerta onClick={() => setFiltroSinal('semSinal')}>
                    Sem sinal ({contagens.semSinal})
                </ChipFiltro>
                <ChipFiltro $ativo={filtroSinal === 'semSinal24h'} $alerta onClick={() => setFiltroSinal('semSinal24h')}>
                    Sem sinal há +24h ({contagens.semSinal24h})
                </ChipFiltro>
                <SelectIgnicao value={filtroIgnicao} onChange={e => setFiltroIgnicao(e.target.value as FiltroIgnicao)}>
                    <option value="todas">Ignição: todas</option>
                    <option value="ligada">Ignição ligada</option>
                    <option value="desligada">Ignição desligada</option>
                </SelectIgnicao>
                <BotaoAjuda type="button" onClick={() => setAjudaAberta(true)}>
                    <FaQuestionCircle size={13} />
                    Entenda os status
                </BotaoAjuda>
                <BotaoAtualizar onClick={onAtualizar} disabled={carregando}>
                    <FaSyncAlt size={12} className={carregando ? 'animate-spin' : ''} />
                    Atualizar
                </BotaoAtualizar>
            </BarraFiltros>

            <Tabela>
                <table>
                    <thead>
                        <tr>
                            <th>Veículo</th>
                            <th>Locatário</th>
                            <th>Endereço atual</th>
                            <th>Última atualização</th>
                            <th>Ignição</th>
                            <th>Sinal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtrados.map(v => {
                            const alerta = semSinalHa24h(v);
                            return (
                                <LinhaVeiculo key={v.placa} $alerta={alerta}>
                                    <td>
                                        <CelVeiculo>
                                            {iconeTipo(v.tipoVeiculo)}
                                            <PlacaModelo>
                                                <b>{v.placa}</b>
                                                <span>{[v.marca, v.modelo].filter(Boolean).join(' ') || '—'}</span>
                                            </PlacaModelo>
                                        </CelVeiculo>
                                    </td>
                                    <td>{v.nomeLocatario || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                                    <td style={{ maxWidth: 320 }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6 }}>
                                            {v.endereco && <FaMapMarkerAlt size={12} color="#94a3b8" style={{ marginTop: 3, flexShrink: 0 }} />}
                                            <span>{renderEndereco(v)}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <Atualizacao $alerta={alerta} title={formatarDataHora(v.ultimaAtualizacao)}>
                                            <b>{tempoRelativo(v.ultimaAtualizacao)}</b>
                                            {v.ultimaAtualizacao && <span>{formatarDataHora(v.ultimaAtualizacao)}</span>}
                                        </Atualizacao>
                                    </td>
                                    <td>{renderIgnicao(v)}</td>
                                    <td>{renderSinal(v)}</td>
                                </LinhaVeiculo>
                            );
                        })}
                    </tbody>
                </table>
                {!carregando && filtrados.length === 0 && (
                    <Vazio>
                        {veiculos.length === 0
                            ? 'Nenhum veículo rastreado encontrado na Plataforma de Rastreio.'
                            : 'Nenhum veículo corresponde aos filtros selecionados.'}
                    </Vazio>
                )}
                {carregando && veiculos.length === 0 && <Vazio>Carregando veículos...</Vazio>}
            </Tabela>

            <RodapeInfo>
                {filtrados.length} de {veiculos.length} veículo(s)
                {ultimaConsulta && ` · Atualizado às ${ultimaConsulta.toLocaleTimeString('pt-BR')}`}
                {' '}· A lista atualiza automaticamente a cada 60 segundos.
            </RodapeInfo>

            {ajudaAberta && (
                <OverlayAjuda onClick={() => setAjudaAberta(false)}>
                    <ModalAjuda onClick={e => e.stopPropagation()}>
                        <ModalTitulo>
                            <h3>Entenda os status dos veículos</h3>
                            <button type="button" onClick={() => setAjudaAberta(false)} aria-label="Fechar">×</button>
                        </ModalTitulo>

                        <ItemAjuda>
                            <ChipStatus $cor="verde">Online</ChipStatus>
                            <p>Comunicou com a plataforma nos últimos minutos.</p>
                        </ItemAjuda>

                        <ItemAjuda>
                            <ChipStatus $cor="ambar">Sem sinal</ChipStatus>
                            <p>
                                Parou de comunicar há pouco tempo. Normal com a <b>ignição desligada</b>:
                                o rastreador economiza bateria e volta a transmitir quando o veículo liga.
                            </p>
                        </ItemAjuda>

                        <ItemAjuda>
                            <ChipStatus $cor="vermelho">Sem sinal</ChipStatus>
                            <p>
                                Mais de <b>24 horas em silêncio</b>. Pode ser rastreador
                                <b> violado</b>, bateria cortada ou chip sem sinal.
                            </p>
                        </ItemAjuda>

                        <ItemAjuda>
                            <ChipStatus $cor="vermelho">Nunca reportou</ChipStatus>
                            <p>Nunca enviou posição. Instalação recente ou equipamento com defeito.</p>
                        </ItemAjuda>

                        <ItemAjuda>
                            <ChipStatus $cor="cinza">Ignição</ChipStatus>
                            <p>Estado do motor na última comunicação do rastreador.</p>
                        </ItemAjuda>

                        <NotaAjuda>
                            O status online/offline vem da Plataforma de Rastreio: o veículo fica offline
                            após poucos minutos sem comunicação.
                        </NotaAjuda>
                    </ModalAjuda>
                </OverlayAjuda>
            )}
        </Wrapper>
    );
}
